import React, { useEffect, useState } from 'react';
import { propertyAPI } from '../../services/api';
import PropertyHouseholdPicker from './PropertyHouseholdPicker';
import PropertyPartyPicker from './PropertyPartyPicker';
import {
  apiErrorMessage,
  OCCUPANCY_OPTIONS,
  SERVICE_TYPE_OPTIONS,
  today,
  VALUATION_SOURCE_OPTIONS,
} from './propertyRegistry';

const useSubmitState = () => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  return { saving, setSaving, error, setError };
};

export function PropertyActionDialog({ title, description, confirmLabel, reasonRequired, busy, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    const closeOnEscape = event => {
      if (event.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [busy, onCancel]);
  const submit = event => {
    event.preventDefault();
    if (reasonRequired && reason.trim().length < 3) {
      setError('किमान ३ अक्षरांचे कारण द्या.');
      return;
    }
    onConfirm(reason.trim());
  };
  return (
    <div className="property-dialog-backdrop" role="presentation" onMouseDown={event => event.target === event.currentTarget && onCancel()}>
      <form className="property-dialog" role="dialog" aria-modal="true" aria-labelledby="property-dialog-title" onSubmit={submit}>
        <h2 id="property-dialog-title">{title}</h2>
        <p>{description}</p>
        <label>कारण {reasonRequired ? '*' : '(ऐच्छिक)'}
          <textarea autoFocus rows="4" maxLength="2000" value={reason} onChange={event => { setReason(event.target.value); setError(''); }} />
        </label>
        {error && <p className="property-field-error" role="alert">{error}</p>}
        <div className="property-dialog-actions"><button type="button" className="property-secondary-button" onClick={onCancel} disabled={busy}>रद्द करा</button><button type="submit" className="property-primary-button" disabled={busy}>{busy ? 'प्रक्रिया सुरू…' : confirmLabel}</button></div>
      </form>
    </div>
  );
}

export function OccupancyForm({ tenantSlug, propertyUid, version, record, onCancel, onSaved }) {
  const state = useSubmitState();
  const editing = Boolean(record);
  const initialSource = record?.party ? 'party' : record?.household_id ? 'household' : 'party';
  const [form, setForm] = useState({
    occupancy_type: record?.occupancy_type || 'owner_occupied',
    occupant_source: initialSource,
    party: record?.party || null,
    household_id: record?.household_id || '',
    is_primary: record?.is_primary ?? true,
    valid_from: record?.valid_from || today(),
    valid_to: record?.valid_to || '',
    status: record?.status || 'active',
    evidence_note: record?.evidence?.note || '',
  });
  const isVacant = form.occupancy_type === 'vacant';
  const allowedStatuses = !editing
    ? [['active', 'सक्रिय'], ['pending', 'प्रलंबित']]
    : record.status === 'pending'
      ? [['pending', 'प्रलंबित'], ['active', 'सक्रिय'], ['ended', 'समाप्त'], ['rejected', 'नामंजूर']]
      : record.status === 'active'
        ? [['active', 'सक्रिय'], ['ended', 'समाप्त']]
        : [[record.status, record.status]];

  const submit = async event => {
    event.preventDefault();
    state.setError('');
    if (!isVacant && form.occupant_source === 'party' && !form.party) return state.setError('वास्तव्य करणारी व्यक्ती किंवा संस्था निवडा.');
    if (!isVacant && form.occupant_source === 'household' && !String(form.household_id).trim()) return state.setError('कुटुंब क्रमांक आवश्यक आहे.');
    if (!form.valid_from) return state.setError('लागू दिनांक आवश्यक आहे.');
    if (form.valid_to && form.valid_to < form.valid_from) return state.setError('समाप्ती दिनांक लागू दिनांकापूर्वी असू शकत नाही.');
    state.setSaving(true);
    try {
      const payload = {
        version,
        occupancy_type: form.occupancy_type,
        party_uid: !isVacant && form.occupant_source === 'party' ? form.party.party_uid : null,
        household_id: !isVacant && form.occupant_source === 'household' ? String(form.household_id).trim() : null,
        is_primary: form.is_primary,
        valid_from: form.valid_from,
        valid_to: form.valid_to || null,
        status: form.status,
        evidence: form.evidence_note ? { note: form.evidence_note.trim() } : {},
      };
      const response = editing
        ? await propertyAPI.updateOccupancy(tenantSlug, propertyUid, record.id, payload)
        : await propertyAPI.createOccupancy(tenantSlug, propertyUid, payload);
      onSaved(response.data, editing ? 'वास्तव्य अद्ययावत केले.' : 'वास्तव्य जोडले.');
    } catch (error) {
      state.setError(apiErrorMessage(error, 'वास्तव्य जतन करता आले नाही.'));
    } finally {
      state.setSaving(false);
    }
  };

  useEffect(() => {
    if (isVacant && form.occupant_source !== 'none') setForm(current => ({ ...current, occupant_source: 'none', party: null, household_id: '' }));
    if (!isVacant && form.occupant_source === 'none') setForm(current => ({ ...current, occupant_source: 'party' }));
  }, [form.occupant_source, isVacant]);

  return (
    <form className="property-inline-editor" onSubmit={submit}>
      <div className="property-inline-editor-heading"><h3>{editing ? 'वास्तव्य संपादित करा' : 'वास्तव्य जोडा'}</h3><button type="button" className="property-icon-button" onClick={onCancel} aria-label="वास्तव्य फॉर्म बंद करा">×</button></div>
      {state.error && <p className="property-alert error" role="alert">{state.error}</p>}
      <div className="property-form-grid">
        <label>वास्तव्य प्रकार *<select value={form.occupancy_type} onChange={event => setForm(current => ({ ...current, occupancy_type: event.target.value }))}>{OCCUPANCY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        {!isVacant && <label>रहिवासी संदर्भ *<select value={form.occupant_source} onChange={event => setForm(current => ({ ...current, occupant_source: event.target.value, party: null, household_id: '' }))}><option value="party">व्यक्ती किंवा संस्था</option><option value="household">कुटुंब</option></select></label>}
        {!isVacant && form.occupant_source === 'party' && <PropertyPartyPicker tenantSlug={tenantSlug} value={form.party} onChange={party => setForm(current => ({ ...current, party }))} required />}
        {!isVacant && form.occupant_source === 'household' && <PropertyHouseholdPicker tenantSlug={tenantSlug} value={form.household_id} onChange={householdId => setForm(current => ({ ...current, household_id: householdId }))} required />}
        <label>लागू दिनांक *<input type="date" value={form.valid_from} onChange={event => setForm(current => ({ ...current, valid_from: event.target.value }))} required /></label>
        <label>समाप्ती दिनांक<input type="date" min={form.valid_from} value={form.valid_to} onChange={event => setForm(current => ({ ...current, valid_to: event.target.value }))} /></label>
        <label>स्थिती<select value={form.status} onChange={event => setForm(current => ({ ...current, status: event.target.value }))}>{allowedStatuses.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label className="property-check-label"><input type="checkbox" checked={form.is_primary} onChange={event => setForm(current => ({ ...current, is_primary: event.target.checked }))} /> प्राथमिक वास्तव्यास</label>
        <label className="property-wide-field">पुराव्याची नोंद<textarea rows="2" maxLength="1000" value={form.evidence_note} onChange={event => setForm(current => ({ ...current, evidence_note: event.target.value }))} /></label>
      </div>
      <div className="property-inline-actions"><button type="button" className="property-secondary-button" onClick={onCancel}>रद्द करा</button><button className="property-primary-button" type="submit" disabled={state.saving}>{state.saving ? 'जतन होत आहे…' : 'जतन करा'}</button></div>
    </form>
  );
}

export function ValuationForm({ tenantSlug, propertyUid, version, record, onCancel, onSaved }) {
  const state = useSubmitState();
  const editing = Boolean(record);
  const [form, setForm] = useState({
    valuation_date: record?.valuation_date || today(),
    effective_from: record?.effective_from || today(),
    effective_to: record?.effective_to || '',
    land_value: record?.land_value ?? '',
    building_value: record?.building_value ?? '',
    assessed_value: record?.assessed_value ?? '',
    annual_rental_value: record?.annual_rental_value ?? '',
    source: record?.source || 'manual',
    status: record?.status === 'rejected' ? 'draft' : record?.status || 'draft',
    notes: record?.notes || '',
  });
  const allowedStatuses = !editing || record.status === 'draft'
    ? [['draft', 'मसुदा'], ['pending_approval', 'मंजुरीसाठी पाठवा']]
    : record.status === 'pending_approval'
      ? [['pending_approval', 'मंजुरी प्रलंबित'], ['draft', 'मसुद्याकडे परत']]
      : record.status === 'rejected'
        ? [['draft', 'मसुदा म्हणून पुन्हा उघडा']]
        : [[record.status, record.status]];
  const submit = async event => {
    event.preventDefault();
    state.setError('');
    if (!form.valuation_date || !form.effective_from) return state.setError('मूल्यांकन दिनांक आणि लागू दिनांक आवश्यक आहेत.');
    if (form.valuation_date > form.effective_from) return state.setError('मूल्यांकन दिनांक लागू दिनांकानंतर असू शकत नाही.');
    if (form.effective_to && form.effective_to < form.effective_from) return state.setError('समाप्ती दिनांक लागू दिनांकापूर्वी असू शकत नाही.');
    const amountFields = ['land_value', 'building_value', 'assessed_value', 'annual_rental_value'];
    if (!amountFields.some(field => form[field] !== '' && Number(form[field]) >= 0)) return state.setError('किमान एक मूल्यांकन रक्कम आवश्यक आहे.');
    if (amountFields.some(field => form[field] !== '' && (!Number.isFinite(Number(form[field])) || Number(form[field]) < 0))) return state.setError('मूल्यांकन रक्कम ऋण असू शकत नाही.');
    state.setSaving(true);
    try {
      const payload = {
        version,
        valuation_date: form.valuation_date,
        effective_from: form.effective_from,
        effective_to: form.effective_to || null,
        land_value: form.land_value === '' ? null : Number(form.land_value),
        building_value: form.building_value === '' ? null : Number(form.building_value),
        assessed_value: form.assessed_value === '' ? null : Number(form.assessed_value),
        annual_rental_value: form.annual_rental_value === '' ? null : Number(form.annual_rental_value),
        source: form.source,
        status: form.status,
        notes: form.notes || null,
      };
      const response = editing
        ? await propertyAPI.updateValuation(tenantSlug, propertyUid, record.valuation_uid, payload)
        : await propertyAPI.createValuation(tenantSlug, propertyUid, payload);
      onSaved(response.data, editing ? 'मूल्यांकन अद्ययावत केले.' : 'मूल्यांकन जोडले.');
    } catch (error) {
      state.setError(apiErrorMessage(error, 'मूल्यांकन जतन करता आले नाही.'));
    } finally {
      state.setSaving(false);
    }
  };
  return (
    <form className="property-inline-editor" onSubmit={submit}>
      <div className="property-inline-editor-heading"><h3>{editing ? 'मूल्यांकन संपादित करा' : 'मूल्यांकन जोडा'}</h3><button type="button" className="property-icon-button" onClick={onCancel} aria-label="मूल्यांकन फॉर्म बंद करा">×</button></div>
      {state.error && <p className="property-alert error" role="alert">{state.error}</p>}
      <div className="property-form-grid">
        <label>मूल्यांकन दिनांक *<input type="date" value={form.valuation_date} onChange={event => setForm(current => ({ ...current, valuation_date: event.target.value }))} required /></label>
        <label>लागू दिनांक *<input type="date" value={form.effective_from} onChange={event => setForm(current => ({ ...current, effective_from: event.target.value }))} required /></label>
        <label>समाप्ती दिनांक<input type="date" min={form.effective_from} value={form.effective_to} onChange={event => setForm(current => ({ ...current, effective_to: event.target.value }))} /></label>
        <label>स्रोत<select value={form.source} onChange={event => setForm(current => ({ ...current, source: event.target.value }))}>{VALUATION_SOURCE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label>जमीन मूल्य (₹)<input type="number" min="0" step="0.01" value={form.land_value} onChange={event => setForm(current => ({ ...current, land_value: event.target.value }))} /></label>
        <label>इमारत मूल्य (₹)<input type="number" min="0" step="0.01" value={form.building_value} onChange={event => setForm(current => ({ ...current, building_value: event.target.value }))} /></label>
        <label>निर्धारित मूल्य (₹)<input type="number" min="0" step="0.01" value={form.assessed_value} onChange={event => setForm(current => ({ ...current, assessed_value: event.target.value }))} /></label>
        <label>वार्षिक भाडेमूल्य (₹)<input type="number" min="0" step="0.01" value={form.annual_rental_value} onChange={event => setForm(current => ({ ...current, annual_rental_value: event.target.value }))} /></label>
        <label>स्थिती<select value={form.status} onChange={event => setForm(current => ({ ...current, status: event.target.value }))}>{allowedStatuses.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label className="property-wide-field">टीप<textarea rows="3" maxLength="5000" value={form.notes} onChange={event => setForm(current => ({ ...current, notes: event.target.value }))} /></label>
      </div>
      <div className="property-inline-actions"><button type="button" className="property-secondary-button" onClick={onCancel}>रद्द करा</button><button className="property-primary-button" type="submit" disabled={state.saving}>{state.saving ? 'जतन होत आहे…' : 'जतन करा'}</button></div>
    </form>
  );
}

export function ServiceConnectionForm({ tenantSlug, propertyUid, version, record, onCancel, onSaved }) {
  const state = useSubmitState();
  const editing = Boolean(record);
  const [form, setForm] = useState({
    service_type: record?.service_type || 'water',
    connection_number: record?.connection_number || '',
    tariff_class: record?.tariff_class || '',
    meter_number: record?.meter_number || '',
    connection_date: record?.connection_date || '',
    status: record?.status || 'pending',
  });
  const transitions = {
    pending: ['pending', 'active', 'archived'],
    active: ['active', 'suspended', 'disconnected', 'archived'],
    suspended: ['suspended', 'active', 'disconnected', 'archived'],
    disconnected: ['disconnected', 'archived'],
    archived: ['archived'],
  };
  const statuses = editing ? transitions[record.status] || [record.status] : ['pending'];
  const submit = async event => {
    event.preventDefault();
    state.setError('');
    if (!form.connection_number.trim()) return state.setError('जोडणी क्रमांक आवश्यक आहे.');
    state.setSaving(true);
    try {
      const payload = {
        version,
        service_type: form.service_type,
        connection_number: form.connection_number.trim(),
        tariff_class: form.tariff_class || null,
        meter_number: form.meter_number || null,
        connection_date: form.connection_date || null,
        status: form.status,
      };
      const response = editing
        ? await propertyAPI.updateServiceConnection(tenantSlug, propertyUid, record.connection_uid, payload)
        : await propertyAPI.createServiceConnection(tenantSlug, propertyUid, payload);
      onSaved(response.data, editing ? 'सेवा जोडणी अद्ययावत केली.' : 'सेवा जोडणी जोडली.');
    } catch (error) {
      state.setError(apiErrorMessage(error, 'सेवा जोडणी जतन करता आली नाही.'));
    } finally {
      state.setSaving(false);
    }
  };
  return (
    <form className="property-inline-editor" onSubmit={submit}>
      <div className="property-inline-editor-heading"><h3>{editing ? 'सेवा जोडणी संपादित करा' : 'सेवा जोडणी जोडा'}</h3><button type="button" className="property-icon-button" onClick={onCancel} aria-label="सेवा जोडणी फॉर्म बंद करा">×</button></div>
      {state.error && <p className="property-alert error" role="alert">{state.error}</p>}
      <p className="property-form-note">नवीन जोडणी आणि सक्रियतेची विनंती दुसऱ्या अधिकाऱ्याने मालमत्ता पडताळेपर्यंत प्रलंबित राहते. पडताळलेल्या मालमत्तेचा दरवर्ग किंवा मीटर क्रमांक बदलल्यास ती पुन्हा पडताळणीसाठी जाते.</p>
      <div className="property-form-grid">
        <label>सेवा *<select value={form.service_type} disabled={editing} onChange={event => setForm(current => ({ ...current, service_type: event.target.value }))}>{SERVICE_TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label>जोडणी क्रमांक *<input required maxLength="100" value={form.connection_number} onChange={event => setForm(current => ({ ...current, connection_number: event.target.value }))} /></label>
        <label>दर वर्ग<input maxLength="100" value={form.tariff_class} onChange={event => setForm(current => ({ ...current, tariff_class: event.target.value }))} /></label>
        <label>मीटर क्रमांक<input maxLength="100" value={form.meter_number} onChange={event => setForm(current => ({ ...current, meter_number: event.target.value }))} /></label>
        <label>जोडणी दिनांक<input type="date" value={form.connection_date} onChange={event => setForm(current => ({ ...current, connection_date: event.target.value }))} /></label>
        <label>स्थिती<select value={form.status} onChange={event => setForm(current => ({ ...current, status: event.target.value }))}>{statuses.map(value => <option key={value} value={value}>{({ pending:'प्रलंबित', active:'सक्रिय', suspended:'स्थगित', disconnected:'जोडणी बंद', archived:'संग्रहित' })[value] || 'स्थिती उपलब्ध नाही'}</option>)}</select></label>
      </div>
      <div className="property-inline-actions"><button type="button" className="property-secondary-button" onClick={onCancel}>रद्द करा</button><button className="property-primary-button" type="submit" disabled={state.saving}>{state.saving ? 'जतन होत आहे…' : 'जतन करा'}</button></div>
    </form>
  );
}
